import { assertConfigured, config } from './config';
import { yieldToMain } from './mainThread';
import type {
  LatLng,
  OrsDirectionsResponse,
  Profile,
  RouteOptions,
  RoutePreference,
  RouteResult,
} from './types';
import { DEFAULT_ROUTE_OPTIONS } from './types';

/**
 * Compass bearing from one point to another, in degrees (0 = north, 90 = east,
 * clockwise positive). Uses the great-circle formula; correct at any latitude
 * because the routing area is small (<100 km typically).
 */
export function bearingDegrees(from: LatLng, to: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(from.lat);
  const φ2 = toRad(to.lat);
  const Δλ = toRad(to.lng - from.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * Smooth two angles with wrap-around awareness. Linear averaging breaks at
 * the 0/360 boundary; we average the underlying sinusoid components and
 * convert back. Returns 0..360 degrees.
 */
export function smaBearing(prev: number, next: number, alpha: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const sinSum =
    Math.sin(toRad(prev)) * (1 - alpha) + Math.sin(toRad(next)) * alpha;
  const cosSum =
    Math.cos(toRad(prev)) * (1 - alpha) + Math.cos(toRad(next)) * alpha;
  return (toDeg(Math.atan2(sinSum, cosSum)) + 360) % 360;
}

/**
 * Routing layer.
 *
 * - `getRoute` calls ORS directions for origin → ordered stops.
 * - `Rerouter` observes fixes from the app's single GPS watch and decides
 *   when to trigger an automatic recompute. Thresholds are documented
 *   inline so we can tune them as we test.
 */

const ENDPOINT = (profile: Profile) => `/v2/directions/${profile}/geojson`;

/** Max intermediate + final destinations in one plan (GPS start is separate). */
export const MAX_ROUTE_STOPS = 3;

type DecodeOk = {
  ok: true;
  routes: Array<{
    coords: Float64Array;
    pointCount: number;
    steps: RouteResult['steps'];
    distance: number;
    duration: number;
  }>;
};

type DecodeErr = { ok: false; error: string };

/** Straight-line cap for ORS alternative_routes (~100 km server limit). */
const ALT_ROUTES_MAX_BEELINE_M = 85_000;

export type RoutePlanResult = {
  primary: RouteResult;
  alternatives: RouteResult[];
};

function decodeDirectionsInWorker(
  rawJson: string,
  signal?: AbortSignal,
): Promise<DecodeOk> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    let worker: Worker;
    try {
      worker = new Worker(new URL('./routeDecode.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
      return;
    }

    const onAbort = (): void => {
      worker.terminate();
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });

    const fail = (message: string) => {
      signal?.removeEventListener('abort', onAbort);
      worker.terminate();
      reject(new Error(message));
    };
    worker.onmessage = (event: MessageEvent<DecodeOk | DecodeErr>) => {
      signal?.removeEventListener('abort', onAbort);
      const msg = event.data;
      worker.terminate();
      if (signal?.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      if (!msg || msg.ok !== true) {
        fail(msg && 'error' in msg ? msg.error : 'Route decode failed.');
        return;
      }
      resolve(msg);
    };
    worker.onerror = (event) => {
      fail(event.message || 'Route decode worker failed.');
    };
    worker.postMessage(rawJson);
  });
}

function decodeDirectionsSync(rawJson: string): DecodeOk {
  const data = JSON.parse(rawJson) as OrsDirectionsResponse;
  const features = data.features;
  if (!Array.isArray(features) || features.length === 0) {
    throw new Error('A routing szolgáltatás nem adott vissza útvonalat.');
  }
  const routes: DecodeOk['routes'] = [];
  for (const feature of features) {
    const pairs = feature.geometry?.coordinates;
    if (!Array.isArray(pairs) || pairs.length < 2) continue;
    const pointCount = pairs.length;
    const coords = new Float64Array(pointCount * 2);
    for (let i = 0; i < pointCount; i++) {
      const p = pairs[i]!;
      coords[i * 2] = p[0]!;
      coords[i * 2 + 1] = p[1]!;
    }
    const steps = (feature.properties?.segments ?? []).flatMap((s) =>
      s.steps.map((step) => mapOrsStep(step)),
    );
    routes.push({
      coords,
      pointCount,
      steps,
      distance: feature.properties?.summary?.distance ?? 0,
      duration: feature.properties?.summary?.duration ?? 0,
    });
  }
  if (routes.length === 0) {
    throw new Error('A routing szolgáltatás nem adott vissza útvonalat.');
  }
  return { ok: true, routes };
}

function mapOrsStep(
  step: OrsDirectionsResponse['features'][number]['properties']['segments'][number]['steps'][number],
): RouteResult['steps'][number] {
  const man = step.maneuver;
  const loc = man?.location;
  const maneuver_location =
    Array.isArray(loc) && loc.length >= 2 && Number.isFinite(loc[0]) && Number.isFinite(loc[1])
      ? { lng: Number(loc[0]), lat: Number(loc[1]) }
      : undefined;
  const exit_bearings = Array.isArray(step.exit_bearings)
    ? step.exit_bearings.filter((b): b is number => Number.isFinite(b))
    : undefined;
  return {
    instruction: step.instruction ?? '',
    name: step.name ?? '',
    distance: step.distance,
    duration: step.duration,
    way_points: step.way_points,
    type: step.type,
    exit_number: step.exit_number,
    ...(exit_bearings && exit_bearings.length > 0 ? { exit_bearings } : {}),
    ...(Number.isFinite(man?.bearing_before)
      ? { bearing_before: Number(man!.bearing_before) }
      : {}),
    ...(Number.isFinite(man?.bearing_after)
      ? { bearing_after: Number(man!.bearing_after) }
      : {}),
    ...(maneuver_location ? { maneuver_location } : {}),
  };
}

const DRIVING_PREFERENCES: readonly RoutePreference[] = [
  'recommended',
  'fastest',
  'shortest',
];

export async function getRoute(
  start: LatLng,
  stops: LatLng[],
  profile: Profile,
  routeOptions: RouteOptions = DEFAULT_ROUTE_OPTIONS,
  signal?: AbortSignal,
): Promise<RouteResult> {
  const planned = await planRoutes(start, stops, profile, routeOptions, signal, {
    alternatives: false,
  });
  return planned.primary;
}

export async function planRoutes(
  start: LatLng,
  stops: LatLng[],
  profile: Profile,
  routeOptions: RouteOptions = DEFAULT_ROUTE_OPTIONS,
  signal?: AbortSignal,
  opts: { alternatives?: boolean } = {},
): Promise<RoutePlanResult> {
  assertConfigured();
  throwIfAborted(signal);

  if (stops.length === 0) {
    throw new Error('Legalabb egy celpont kell az utvonalhoz.');
  }

  const wantDrivingPreferenceAlts =
    opts.alternatives === true && profile === 'driving-car';

  if (wantDrivingPreferenceAlts) {
    return planDrivingPreferenceRoutes(start, stops, profile, routeOptions, signal);
  }

  const wantOrsAlternatives =
    opts.alternatives === true &&
    profile !== 'driving-car' &&
    stops.length === 1 &&
    haversineMeters(start, stops[0]!) <= ALT_ROUTES_MAX_BEELINE_M;

  const routes = await fetchDirectionsRoutes(
    start,
    stops,
    profile,
    routeOptions,
    signal,
    wantOrsAlternatives,
  );
  const primary = routes[0];
  if (!primary) {
    throw new Error('A routing szolgáltatás nem adott vissza útvonalat.');
  }
  return {
    primary,
    alternatives: routes.slice(1),
  };
}

async function planDrivingPreferenceRoutes(
  start: LatLng,
  stops: LatLng[],
  profile: Profile,
  routeOptions: RouteOptions,
  signal?: AbortSignal,
): Promise<RoutePlanResult> {
  const settled = await Promise.all(
    DRIVING_PREFERENCES.map(async (preference) => {
      try {
        const routes = await fetchDirectionsRoutes(
          start,
          stops,
          profile,
          { ...routeOptions, preference },
          signal,
          false,
        );
        const route = routes[0];
        if (!route) return null;
        return { preference, route };
      } catch (err) {
        if (isAbortError(err) || signal?.aborted) throw err;
        return null;
      }
    }),
  );

  throwIfAborted(signal);
  const byPref = new Map<RoutePreference, RouteResult>();
  for (const item of settled) {
    if (item) byPref.set(item.preference, item.route);
  }

  const primary =
    byPref.get(routeOptions.preference) ??
    byPref.get('recommended') ??
    byPref.get('fastest') ??
    byPref.get('shortest');
  if (!primary) {
    throw new Error('A routing szolgáltatás nem adott vissza útvonalat.');
  }

  const alternatives: RouteResult[] = [];
  for (const preference of DRIVING_PREFERENCES) {
    if (preference === routeOptions.preference) continue;
    const candidate = byPref.get(preference);
    if (!candidate || candidate.coords.length < 2) continue;
    alternatives.push(candidate);
  }

  return { primary, alternatives };
}

async function fetchDirectionsRoutes(
  start: LatLng,
  stops: LatLng[],
  profile: Profile,
  routeOptions: RouteOptions,
  signal: AbortSignal | undefined,
  includeOrsAlternatives: boolean,
): Promise<RouteResult[]> {
  const coordinates: Array<[number, number]> = [
    [start.lng, start.lat],
    ...stops.map((s) => [s.lng, s.lat] as [number, number]),
  ];

  const avoid = filterAvoidForProfile(profile, routeOptions.avoid);
  const body: Record<string, unknown> = {
    coordinates,
    preference: routeOptions.preference,
    instructions: true,
    language: 'en',
    geometry: true,
    elevation: false,
    roundabout_exits: true,
    maneuvers: true,
  };
  if (avoid.length > 0) {
    body.options = { avoid_features: avoid };
  }
  if (includeOrsAlternatives) {
    body.alternative_routes = {
      target_count: 2,
      weight_factor: 1.4,
      share_factor: 0.6,
    };
  }

  const rawJson = await fetchDirectionsText(`${ENDPOINT(profile)}`, body, signal);
  throwIfAborted(signal);
  await yieldToMain();
  throwIfAborted(signal);

  let decoded: DecodeOk;
  try {
    decoded = await decodeDirectionsInWorker(rawJson, signal);
  } catch (err) {
    if (isAbortError(err) || signal?.aborted) {
      throw err instanceof DOMException
        ? err
        : new DOMException('Aborted', 'AbortError');
    }
    throwIfAborted(signal);
    await yieldToMain();
    decoded = decodeDirectionsSync(rawJson);
  }

  throwIfAborted(signal);
  const routes: RouteResult[] = [];
  for (const part of decoded.routes) {
    throwIfAborted(signal);
    await yieldToMain();
    const coords: LatLng[] = new Array(part.pointCount);
    const packed = part.coords;
    for (let i = 0; i < part.pointCount; i++) {
      coords[i] = { lng: packed[i * 2]!, lat: packed[i * 2 + 1]! };
      if (i > 0 && i % 250 === 0) {
        throwIfAborted(signal);
        await yieldToMain();
      }
    }
    routes.push({
      coords,
      distance: part.distance,
      duration: part.duration,
      steps: part.steps,
    });
  }
  return routes;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
}

function filterAvoidForProfile(
  profile: Profile,
  avoid: RouteOptions['avoid'],
): RouteOptions['avoid'] {
  if (profile === 'driving-car') return avoid;
  return avoid.filter((f) => f === 'ferries');
}

async function fetchDirectionsText(
  path: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<string> {
  const init: RequestInit = {
    method: 'POST',
    headers: {
      Accept: 'application/geo+json,application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    signal,
  };

  const url =
    config.mode === 'proxy'
      ? `${config.proxyUrl}${path}`
      : `https://api.openrouteservice.org${path}`;

  if (config.mode === 'direct') {
    (init.headers as Record<string, string>).Authorization = config.orsKey;
  }

  const res = await fetch(url, init);
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const errBody = (await res.json()) as { error?: string };
      if (errBody?.error) detail += ` — ${errBody.error}`;
    } catch {
      /* ignore */
    }
    throw new Error(`Útvonaltervezés sikertelen: ${detail}`);
  }

  return res.text();
}

/**
 * Look up the upcoming turn-by-turn step the user should act on next.
 *
 * ORS/GraphHopper steps put the maneuver at `way_points[0]`. While the
 * projection sits inside a step, that step's instruction is already behind
 * the driver — the next decision is the following step. We therefore pick
 * the first step whose start vertex is still at/ahead of the user.
 *
 * `remainingMeters` / `turnAt` refer to that upcoming maneuver point.
 */
export function findCurrentStep(
  progress: RouteProgress,
  steps: RouteResult['steps'],
  route: LatLng[],
): {
  step: RouteResult['steps'][number];
  remainingMeters: number;
  index: number;
  turnAt: LatLng | null;
} | null {
  if (steps.length === 0) return null;

  const atIdx = Math.max(0, Math.min(progress.segmentIndex, route.length - 1));
  let upcomingIndex = steps.length - 1;
  for (let i = 0; i < steps.length; i++) {
    const start = steps[i]?.way_points?.[0];
    if (start === undefined) continue;
    if (start >= atIdx) {
      upcomingIndex = i;
      break;
    }
  }

  const step = steps[upcomingIndex];
  if (!step) return null;

  const turnIdx = step.way_points
    ? Math.min(step.way_points[0], Math.max(0, route.length - 1))
    : Math.max(0, route.length - 1);
  const turnAt = route[turnIdx] ?? null;
  const remainingMeters = metersAlongRoute(route, progress.traveledMeters, turnIdx);

  return { step, remainingMeters, index: upcomingIndex, turnAt };
}

/**
 * Along-route metres from a traveled-offset (metres from route[0]) to the
 * coordinate at `toIndex`. Clamped to ≥0.
 */
function metersAlongRoute(route: LatLng[], fromTraveledMeters: number, toIndex: number): number {
  if (route.length < 2 || toIndex <= 0) return 0;
  let along = 0;
  const last = Math.min(toIndex, route.length - 1);
  for (let i = 1; i <= last; i++) {
    along += haversineMeters(route[i - 1]!, route[i]!);
  }
  return Math.max(0, along - fromTraveledMeters);
}

// ---------------------------------------------------------------------------
// Off-route detection (rerouting)
// ---------------------------------------------------------------------------

/**
 * Minimum distance from the user's position to the route polyline before we
 * consider them "off route". 50 m is a sane default for driving and cycling
 * (≈ a lane width); walking can use a smaller value via the constructor.
 */
const DEFAULT_OFF_ROUTE_METERS = 50;

/**
 * Minimum number of seconds between automatic reroutes. Stops GPS jitter
 * and short detours from spamming the API.
 */
const DEFAULT_REROUTE_COOLDOWN_MS = 30_000;

/**
 * Haversine distance between two coordinates, in metres.
 */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Perpendicular distance from a point P to a line segment AB on a flat
 * (lat/lng) plane projected via equirectangular scaling. Good enough for
 * off-route checks at typical city distances (<50 km).
 */
function distancePointToSegmentMeters(p: LatLng, a: LatLng, b: LatLng): number {
  const meanLatRad = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const cosMeanLat = Math.cos(meanLatRad);
  const kx = cosMeanLat; // longitude scale factor

  const ax = a.lng * kx;
  const ay = a.lat;
  const bx = b.lng * kx;
  const by = b.lat;
  const px = p.lng * kx;
  const py = p.lat;

  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  let t = lenSq === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const closestX = ax + t * dx;
  const closestY = ay + t * dy;

  // 1 degree latitude ≈ 111_320 m
  const dyMeters = (closestY - py) * 111_320;
  const dxMeters = (closestX - px) * 111_320 * cosMeanLat;

  return Math.sqrt(dyMeters * dyMeters + dxMeters * dxMeters);
}

/**
 * Minimum distance (in metres) from a point to any segment of the polyline.
 */
export function distanceToRouteMeters(p: LatLng, route: LatLng[]): number {
  if (route.length === 0) return Infinity;
  if (route.length === 1) return haversineMeters(p, route[0]!);

  let min = Infinity;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!;
    const b = route[i]!;
    const d = distancePointToSegmentMeters(p, a, b);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Result of projecting a point onto a route polyline.
 *
 * `traveledMeters` is the distance from `route[0]` along the polyline to
 * the closest point on the polyline to `p`. `totalMeters` is the entire
 * polyline length. `remainingMeters = totalMeters - traveledMeters`.
 */
export interface RouteProgress {
  traveledMeters: number;
  totalMeters: number;
  /** Index of the segment whose projection is closest to `p` (last segment). */
  segmentIndex: number;
}

/**
 * Project a point onto the polyline and measure how far along the route
 * the closest foot-of-perpendicular lies. Used to compute remaining
 * distance / duration for the HUD during navigation.
 *
 * Algorithm: O(N) — single pass to compute segment lengths, single pass
 * to find the closest segment, single pass to sum travelled distance.
 * Prefer `RouteIndex` on the live GPS path — it reuses lengths and a
 * moving search window so long routes stay interactive.
 */
export function projectOntoRoute(p: LatLng, route: LatLng[]): RouteProgress {
  if (route.length === 0) return { traveledMeters: 0, totalMeters: 0, segmentIndex: 0 };
  if (route.length === 1) return { traveledMeters: 0, totalMeters: 0, segmentIndex: 0 };

  const segLengths: number[] = [];
  let total = 0;
  for (let i = 1; i < route.length; i++) {
    const seg = haversineMeters(route[i - 1]!, route[i]!);
    segLengths.push(seg);
    total += seg;
  }

  let bestI = 0;
  let bestT = 0;
  let bestDistSq = Infinity;
  const px = p.lng;
  const py = p.lat;
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!;
    const b = route[i]!;
    const meanLatRad = ((a.lat + b.lat) / 2) * (Math.PI / 180);
    const cosMeanLat = Math.cos(meanLatRad);
    const kx = cosMeanLat;
    const ax = a.lng * kx;
    const ay = a.lat;
    const bx = b.lng * kx;
    const by = b.lat;
    const ppx = px * kx;

    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq === 0 ? 0 : ((ppx - ax) * dx + (py - ay) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = ax + t * dx;
    const cy = ay + t * dy;
    const xd = cx - ppx;
    const yd = cy - py;
    const distSq = xd * xd + yd * yd;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestI = i - 1;
      bestT = t;
    }
  }

  let traveled = 0;
  for (let j = 0; j < bestI; j++) traveled += segLengths[j]!;
  traveled += bestT * (segLengths[bestI] ?? 0);

  return { traveledMeters: traveled, totalMeters: total, segmentIndex: bestI };
}

/**
 * Cached polyline metrics + moving-window projection for live navigation.
 * Build once per route update; reuse on every GPS tick.
 */
export class RouteIndex {
  readonly route: LatLng[];
  private readonly segLengths: Float64Array;
  /** Distance along route to vertex `i` (prefix[0] = 0). */
  private readonly prefix: Float64Array;
  readonly totalMeters: number;
  private hintSeg = 0;

  constructor(route: LatLng[]) {
    this.route = route;
    const n = Math.max(0, route.length - 1);
    this.segLengths = new Float64Array(n);
    this.prefix = new Float64Array(route.length);
    let total = 0;
    for (let i = 1; i < route.length; i++) {
      const seg = haversineMeters(route[i - 1]!, route[i]!);
      this.segLengths[i - 1] = seg;
      total += seg;
      this.prefix[i] = total;
    }
    this.totalMeters = total;
  }

  /** Metres from the point to the nearest polyline location. */
  distanceMeters(p: LatLng): number {
    return this.locate(p).distMeters;
  }

  project(p: LatLng): RouteProgress {
    const hit = this.locate(p);
    return {
      traveledMeters: hit.traveledMeters,
      totalMeters: hit.totalMeters,
      segmentIndex: hit.segmentIndex,
    };
  }

  locate(p: LatLng): RouteProgress & { distMeters: number } {
    const hit = this.projectDetailed(p);
    return {
      traveledMeters: hit.traveledMeters,
      totalMeters: this.totalMeters,
      segmentIndex: hit.segmentIndex,
      distMeters: hit.distMeters,
    };
  }

  interpolate(meters: number): LatLng {
    const route = this.route;
    if (route.length === 0) return { lat: 0, lng: 0 };
    if (route.length === 1) return route[0]!;
    const target = Math.max(0, Math.min(meters, this.totalMeters));
    let lo = 0;
    let hi = route.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.prefix[mid]! < target) lo = mid + 1;
      else hi = mid;
    }
    const i = Math.max(1, lo);
    const a = route[i - 1]!;
    const b = route[i]!;
    const segStart = this.prefix[i - 1]!;
    const seg = this.segLengths[i - 1] ?? 0;
    const t = seg === 0 ? 0 : Math.min(1, (target - segStart) / seg);
    return {
      lat: a.lat + (b.lat - a.lat) * t,
      lng: a.lng + (b.lng - a.lng) * t,
    };
  }

  bearingAhead(traveledMeters: number, lookAheadMeters = 40): number {
    if (this.route.length < 2) return 0;
    const from = this.interpolate(traveledMeters);
    const to = this.interpolate(traveledMeters + lookAheadMeters);
    if (haversineMeters(from, to) < 0.5) {
      const a = this.route[this.route.length - 2]!;
      const b = this.route[this.route.length - 1]!;
      return bearingDegrees(a, b);
    }
    return bearingDegrees(from, to);
  }

  splitAt(traveledMeters: number): { traveled: LatLng[]; remaining: LatLng[] } {
    const route = this.route;
    if (route.length === 0) return { traveled: [], remaining: [] };
    if (route.length === 1) {
      return { traveled: [route[0]!], remaining: [route[0]!] };
    }
    const at = this.interpolate(traveledMeters);
    const target = Math.max(0, Math.min(traveledMeters, this.totalMeters));
    let lo = 0;
    let hi = route.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.prefix[mid]! < target) lo = mid + 1;
      else hi = mid;
    }
    const segI = Math.max(0, lo - 1);
    return {
      traveled: [...route.slice(0, segI + 1), at],
      remaining: [at, ...route.slice(segI + 1)],
    };
  }

  private projectDetailed(p: LatLng): {
    traveledMeters: number;
    segmentIndex: number;
    distMeters: number;
  } {
    const route = this.route;
    if (route.length === 0) {
      return { traveledMeters: 0, segmentIndex: 0, distMeters: Infinity };
    }
    if (route.length === 1) {
      return {
        traveledMeters: 0,
        segmentIndex: 0,
        distMeters: haversineMeters(p, route[0]!),
      };
    }

    const window = 120;
    const start = Math.max(1, this.hintSeg - window + 1);
    const end = Math.min(route.length, this.hintSeg + window + 1);
    let hit = this.scanSegments(p, start, end);
    if (hit.distMeters > 80) {
      hit = this.scanSegments(p, 1, route.length);
    }

    this.hintSeg = hit.segmentIndex;
    const traveled =
      this.prefix[hit.segmentIndex]! + hit.t * (this.segLengths[hit.segmentIndex] ?? 0);
    return {
      traveledMeters: traveled,
      segmentIndex: hit.segmentIndex,
      distMeters: hit.distMeters,
    };
  }

  private scanSegments(
    p: LatLng,
    startSeg1: number,
    endSeg1Exclusive: number,
  ): { segmentIndex: number; t: number; distMeters: number } {
    const route = this.route;
    let bestI = Math.max(0, startSeg1 - 1);
    let bestT = 0;
    let bestDistSq = Infinity;
    const py = p.lat;
    const px = p.lng;

    for (let i = startSeg1; i < endSeg1Exclusive; i++) {
      const a = route[i - 1]!;
      const b = route[i]!;
      const meanLatRad = ((a.lat + b.lat) / 2) * (Math.PI / 180);
      const cosMeanLat = Math.cos(meanLatRad);
      const kx = cosMeanLat;
      const ax = a.lng * kx;
      const ay = a.lat;
      const bx = b.lng * kx;
      const by = b.lat;
      const ppx = px * kx;

      const dx = bx - ax;
      const dy = by - ay;
      const lenSq = dx * dx + dy * dy;
      let t = lenSq === 0 ? 0 : ((ppx - ax) * dx + (py - ay) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
      const cx = ax + t * dx;
      const cy = ay + t * dy;
      const xd = cx - ppx;
      const yd = cy - py;
      const distSq = xd * xd + yd * yd;
      if (distSq < bestDistSq) {
        bestDistSq = distSq;
        bestI = i - 1;
        bestT = t;
      }
    }

    const distMeters = Math.sqrt(bestDistSq) * 111_320;
    return { segmentIndex: bestI, t: bestT, distMeters };
  }
}

/** Point on the polyline at `meters` from `route[0]` along the path. */
export function interpolateAlongRoute(route: LatLng[], meters: number): LatLng {
  if (route.length === 0) return { lat: 0, lng: 0 };
  if (route.length === 1) return route[0]!;
  let remaining = Math.max(0, meters);
  for (let i = 1; i < route.length; i++) {
    const a = route[i - 1]!;
    const b = route[i]!;
    const seg = haversineMeters(a, b);
    if (remaining <= seg || i === route.length - 1) {
      const t = seg === 0 ? 0 : Math.min(1, remaining / seg);
      return {
        lat: a.lat + (b.lat - a.lat) * t,
        lng: a.lng + (b.lng - a.lng) * t,
      };
    }
    remaining -= seg;
  }
  return route[route.length - 1]!;
}

/**
 * Forward bearing along the route at `traveledMeters`, looking ahead
 * `lookAheadMeters` so short zigzags don't jitter the camera.
 */
export function routeBearingAhead(
  route: LatLng[],
  traveledMeters: number,
  lookAheadMeters = 40,
): number {
  if (route.length < 2) return 0;
  const from = interpolateAlongRoute(route, traveledMeters);
  const to = interpolateAlongRoute(route, traveledMeters + lookAheadMeters);
  if (haversineMeters(from, to) < 0.5) {
    const a = route[route.length - 2]!;
    const b = route[route.length - 1]!;
    return bearingDegrees(a, b);
  }
  return bearingDegrees(from, to);
}

/** Split the polyline at `traveledMeters` into done + remaining parts. */
export function splitRouteAt(
  route: LatLng[],
  traveledMeters: number,
): { traveled: LatLng[]; remaining: LatLng[] } {
  if (route.length === 0) return { traveled: [], remaining: [] };
  if (route.length === 1) {
    return { traveled: [route[0]!], remaining: [route[0]!] };
  }
  const at = interpolateAlongRoute(route, traveledMeters);
  let acc = 0;
  let segI = 0;
  for (let i = 1; i < route.length; i++) {
    const seg = haversineMeters(route[i - 1]!, route[i]!);
    if (acc + seg >= traveledMeters || i === route.length - 1) {
      segI = i - 1;
      break;
    }
    acc += seg;
  }
  const traveled = [...route.slice(0, segI + 1), at];
  const remaining = [at, ...route.slice(segI + 1)];
  return { traveled, remaining };
}

export interface RerouterOptions {
  /** Off-route distance threshold (m). Default 50. */
  offRouteMeters?: number;
  /** Minimum cooldown between automatic reroutes (ms). Default 30 s. */
  cooldownMs?: number;
}

/**
 * Off-route detector fed by the app's single `watchPosition` stream.
 * Invokes `onRerouteRequest` when the user drifts past the threshold.
 * The host owns GPS and is responsible for recomputing the route.
 */
export class Rerouter {
  private listening = false;
  private route: LatLng[] = [];
  private index: RouteIndex | null = null;
  private lastRerouteAt = 0;
  private readonly offRouteMeters: number;
  private readonly cooldownMs: number;
  private readonly onRerouteRequest: (pos: LatLng) => void;

  constructor(onRerouteRequest: (pos: LatLng) => void, opts: RerouterOptions = {}) {
    this.onRerouteRequest = onRerouteRequest;
    this.offRouteMeters = opts.offRouteMeters ?? DEFAULT_OFF_ROUTE_METERS;
    this.cooldownMs = opts.cooldownMs ?? DEFAULT_REROUTE_COOLDOWN_MS;
  }

  setRoute(route: LatLng[]): void {
    this.route = route;
    this.index = route.length >= 2 ? new RouteIndex(route) : null;
  }

  /** Read-only access to the currently stored polyline (used by the HUD). */
  getRoute(): LatLng[] {
    return this.route;
  }

  getRouteIndex(): RouteIndex | null {
    return this.index;
  }

  start(): void {
    this.listening = true;
  }

  stop(): void {
    this.listening = false;
  }

  /** Feed one GPS fix; no-op unless `start()` has been called. */
  observe(pos: LatLng): void {
    if (!this.listening || this.route.length < 2) return;
    const now = Date.now();
    if (now - this.lastRerouteAt < this.cooldownMs) return;

    const off = this.index
      ? this.index.distanceMeters(pos)
      : distanceToRouteMeters(pos, this.route);
    if (off > this.offRouteMeters) {
      this.lastRerouteAt = now;
      this.onRerouteRequest(pos);
    }
  }
}
